import type { StageMapping } from '../../hooks/usePipeline';
import type { StaffingData, StaffingDeal, StaffingPersonRow } from '../../types';

export const STAFF_TEAM_PARAM = 'staff_team';
export const STAFF_STAGE_PARAM = 'staff_stage';
export const NO_STAGE_VALUE = '__none__';

export interface HierarchicalFilterChild {
  token: string;
  label: string;
}

export interface HierarchicalFilterGroup {
  token: string;
  key: string;
  label: string;
  children: HierarchicalFilterChild[];
}

export interface StaffingFilters {
  team: Set<string>;
  stage: Set<string>;
}

export interface FilteredStaffingData {
  groups: string[];
  group_labels: Record<string, string>;
  people: StaffingPersonRow[];
  portfolio_deals: StaffingDeal[];
  new_deals: StaffingDeal[];
}

const STAGE_GROUP_ORDER = ['portfolio', 'live', 'screening', 'origination', 'other'];
const STAGE_GROUP_LABELS: Record<string, string> = {
  portfolio: 'Portfolio',
  live: 'Live Deals',
  screening: 'Screening',
  origination: 'Origination',
  other: 'Other',
};

function teamGroupToken(groupKey: string): string {
  return `group:${groupKey}`;
}

function userToken(email: string): string {
  return `user:${email.toLowerCase()}`;
}

function stageGroupToken(groupKey: string): string {
  return `group:${groupKey}`;
}

function stageToken(stage: string | null): string {
  return `stage:${stage ?? NO_STAGE_VALUE}`;
}

function stageValueForDeal(deal: StaffingDeal): string {
  return deal.stage ?? NO_STAGE_VALUE;
}

function stageLabel(stage: string): string {
  return stage === NO_STAGE_VALUE ? 'No Stage' : stage;
}

function stageOrderMap(stageMappings: StageMapping[]): Map<string, number> {
  return new Map(stageMappings.map((s) => [s.stage_value, s.order]));
}

function stageSectionMap(stageMappings: StageMapping[]): Map<string, string> {
  return new Map(stageMappings.map((s) => [s.stage_value, s.section]));
}

function dealStageGroupKey(deal: StaffingDeal, stageMappings: StageMapping[]): string {
  if (deal.category === 'portfolio') return 'portfolio';
  if (!deal.stage) return 'other';
  return stageSectionMap(stageMappings).get(deal.stage) ?? 'other';
}

export function emptyStaffingFilters(): StaffingFilters {
  return { team: new Set(), stage: new Set() };
}

export function parseStaffingFiltersFromQuery(search: string): StaffingFilters {
  const params = new URLSearchParams(search);
  return {
    team: new Set(params.getAll(STAFF_TEAM_PARAM).filter(Boolean)),
    stage: new Set(params.getAll(STAFF_STAGE_PARAM).filter(Boolean)),
  };
}

export function staffingFiltersEqual(a: StaffingFilters, b: StaffingFilters): boolean {
  return setEqual(a.team, b.team) && setEqual(a.stage, b.stage);
}

export function staffingFiltersActive(filters: StaffingFilters): boolean {
  return filters.team.size > 0 || filters.stage.size > 0;
}

export function staffingFiltersToSearch(
  search: string,
  filters: StaffingFilters,
  teamGroups: HierarchicalFilterGroup[] = [],
  stageGroups: HierarchicalFilterGroup[] = [],
): string {
  const params = new URLSearchParams(search);
  params.delete(STAFF_TEAM_PARAM);
  params.delete(STAFF_STAGE_PARAM);

  for (const token of orderedSelectedTokens(filters.team, teamGroups)) {
    params.append(STAFF_TEAM_PARAM, token);
  }
  for (const token of orderedSelectedTokens(filters.stage, stageGroups)) {
    params.append(STAFF_STAGE_PARAM, token);
  }

  const next = params.toString();
  return next ? `?${next}` : '';
}

export function buildTeamFilterGroups(data: StaffingData): HierarchicalFilterGroup[] {
  return data.groups
    .map((groupKey) => {
      const people = data.people.filter((p) => p.group === groupKey);
      return {
        token: teamGroupToken(groupKey),
        key: groupKey,
        label: data.group_labels[groupKey] ?? groupKey,
        children: people.map((person) => ({
          token: userToken(person.email),
          label: person.short_name || person.name,
        })),
      };
    })
    .filter((group) => group.children.length > 0);
}

export function buildStageFilterGroups(
  data: StaffingData,
  stageMappings: StageMapping[],
): HierarchicalFilterGroup[] {
  const order = stageOrderMap(stageMappings);
  const grouped = new Map<string, Map<string, HierarchicalFilterChild>>();

  function add(groupKey: string, stage: string | null) {
    if (!grouped.has(groupKey)) grouped.set(groupKey, new Map());
    const value = stage ?? NO_STAGE_VALUE;
    grouped.get(groupKey)?.set(value, {
      token: stageToken(stage),
      label: stageLabel(value),
    });
  }

  if (data.portfolio_deals.length > 0) {
    add('portfolio', 'Portfolio');
  }

  for (const deal of data.new_deals) {
    add(dealStageGroupKey(deal, stageMappings), deal.stage);
  }

  return STAGE_GROUP_ORDER
    .filter((groupKey) => grouped.has(groupKey))
    .map((groupKey) => {
      const children = Array.from(grouped.get(groupKey)?.entries() ?? [])
        .sort(([a], [b]) => {
          const ao = order.get(a) ?? Number.POSITIVE_INFINITY;
          const bo = order.get(b) ?? Number.POSITIVE_INFINITY;
          if (ao !== bo) return ao - bo;
          return stageLabel(a).localeCompare(stageLabel(b));
        })
        .map(([, child]) => child);

      return {
        token: stageGroupToken(groupKey),
        key: groupKey,
        label: STAGE_GROUP_LABELS[groupKey] ?? groupKey,
        children,
      };
    });
}

export function normalizeStaffingFilters(
  filters: StaffingFilters,
  teamGroups: HierarchicalFilterGroup[],
  stageGroups: HierarchicalFilterGroup[],
): StaffingFilters {
  return {
    team: filterValidTokens(filters.team, teamGroups),
    stage: filterValidTokens(filters.stage, stageGroups),
  };
}

export function effectiveChildTokens(
  selected: Set<string>,
  groups: HierarchicalFilterGroup[],
): Set<string> {
  const out = new Set<string>();
  for (const group of groups) {
    if (selected.has(group.token)) {
      for (const child of group.children) out.add(child.token);
      continue;
    }
    for (const child of group.children) {
      if (selected.has(child.token)) out.add(child.token);
    }
  }
  return out;
}

export function applyStaffingFilters(
  data: StaffingData,
  filters: StaffingFilters,
  teamGroups: HierarchicalFilterGroup[],
  stageMappings: StageMapping[],
): FilteredStaffingData {
  const teamActive = filters.team.size > 0;
  const stageActive = filters.stage.size > 0;
  const selectedUserEmails = valuesForPrefix(
    effectiveChildTokens(filters.team, teamGroups),
    'user:',
    true,
  );
  const selectedStageGroups = valuesForPrefix(filters.stage, 'group:');
  const selectedStageValues = valuesForPrefix(filters.stage, 'stage:');

  const stageFilteredPortfolio = data.portfolio_deals.filter((deal) =>
    !stageActive || dealMatchesStageFilter(deal, selectedStageGroups, selectedStageValues, stageMappings),
  );
  const stageFilteredNew = data.new_deals.filter((deal) =>
    !stageActive || dealMatchesStageFilter(deal, selectedStageGroups, selectedStageValues, stageMappings),
  );

  const people = teamActive
    ? data.people.filter((person) => selectedUserEmails.has(person.email.toLowerCase()))
    : data.people;

  const stageDealIds = new Set([
    ...stageFilteredPortfolio.map((d) => d.deal_key),
    ...stageFilteredNew.map((d) => d.deal_key),
  ]);

  let visibleDealIds = stageDealIds;
  if (teamActive) {
    const teamDealIds = new Set<number>();
    for (const person of people) {
      for (const dealId of Object.keys(person.deal_assignments)) {
        teamDealIds.add(Number(dealId));
      }
      for (const dealId of Object.keys(person.deal_activity || {})) {
        teamDealIds.add(Number(dealId));
      }
    }
    visibleDealIds = new Set([...stageDealIds].filter((dealId) => teamDealIds.has(dealId)));
  }

  const portfolio_deals = stageFilteredPortfolio.filter((deal) =>
    visibleDealIds.has(deal.deal_key),
  );
  const new_deals = stageFilteredNew.filter((deal) =>
    visibleDealIds.has(deal.deal_key),
  );
  const portfolioIds = new Set(portfolio_deals.map((d) => d.deal_key));
  const newDealIds = new Set(new_deals.map((d) => d.deal_key));

  const filteredPeople = people.map((person) =>
    restrictPersonToDeals(person, visibleDealIds, portfolioIds, newDealIds),
  );

  return {
    groups: data.groups,
    group_labels: data.group_labels,
    people: filteredPeople,
    portfolio_deals,
    new_deals,
  };
}

function dealMatchesStageFilter(
  deal: StaffingDeal,
  selectedStageGroups: Set<string>,
  selectedStageValues: Set<string>,
  stageMappings: StageMapping[],
): boolean {
  return (
    selectedStageGroups.has(dealStageGroupKey(deal, stageMappings))
    || selectedStageValues.has(stageValueForDeal(deal))
  );
}

function restrictPersonToDeals(
  person: StaffingPersonRow,
  visibleDealIds: Set<number>,
  portfolioIds: Set<number>,
  newDealIds: Set<number>,
): StaffingPersonRow {
  const deal_assignments: Record<number, string> = {};
  for (const [dealId, role] of Object.entries(person.deal_assignments)) {
    const numericId = Number(dealId);
    if (visibleDealIds.has(numericId)) deal_assignments[numericId] = role;
  }

  const deal_activity: Record<number, { meetings: number; emails: number }> = {};
  for (const [dealId, counts] of Object.entries(person.deal_activity || {})) {
    const numericId = Number(dealId);
    if (visibleDealIds.has(numericId)) deal_activity[numericId] = counts;
  }

  const assignedIds = Object.keys(deal_assignments).map(Number);
  const portfolio_count = assignedIds.filter((dealId) => portfolioIds.has(dealId)).length;
  const new_deal_count = assignedIds.filter((dealId) => newDealIds.has(dealId)).length;

  return {
    ...person,
    deal_assignments,
    deal_activity,
    portfolio_count,
    new_deal_count,
    total_count: portfolio_count + new_deal_count,
  };
}

function filterValidTokens(
  selected: Set<string>,
  groups: HierarchicalFilterGroup[],
): Set<string> {
  const valid = validTokens(groups);
  return new Set([...selected].filter((token) => valid.has(token)));
}

function validTokens(groups: HierarchicalFilterGroup[]): Set<string> {
  const out = new Set<string>();
  for (const group of groups) {
    out.add(group.token);
    for (const child of group.children) out.add(child.token);
  }
  return out;
}

function orderedSelectedTokens(
  selected: Set<string>,
  groups: HierarchicalFilterGroup[],
): string[] {
  if (groups.length === 0) return [...selected].sort((a, b) => a.localeCompare(b));

  const out: string[] = [];
  for (const group of groups) {
    if (selected.has(group.token)) out.push(group.token);
    for (const child of group.children) {
      if (selected.has(child.token)) out.push(child.token);
    }
  }
  return out;
}

function valuesForPrefix(tokens: Set<string>, prefix: string, lower = false): Set<string> {
  const out = new Set<string>();
  for (const token of tokens) {
    if (token.startsWith(prefix)) {
      const value = token.slice(prefix.length);
      out.add(lower ? value.toLowerCase() : value);
    }
  }
  return out;
}

function setEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}
